"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.add = add;
exports.closestOnCircle = closestOnCircle;
exports.closestOnSegment = closestOnSegment;
exports.createStringXY = createStringXY;
exports.degreesToStringHDMS = degreesToStringHDMS;
exports.distance = distance;
exports.equals = equals;
exports.format = format;
exports.getWorldsAway = getWorldsAway;
exports.rotate = rotate;
exports.scale = scale;
exports.squaredDistance = squaredDistance;
exports.squaredDistanceToSegment = squaredDistanceToSegment;
exports.toStringHDMS = toStringHDMS;
exports.toStringXY = toStringXY;
exports.wrapX = wrapX;

var _extent = require("./extent.js");

var _math = require("./math.js");

var _string = require("./string.js");

/**
 * @module ol/coordinate
 */

/**
 * An array of numbers representing an xy coordinate. Example: `[16, 48]`.
 * @typedef {Array<number>} Coordinate
 * @api
 */

/**
 * A function that takes a {@link module:ol/coordinate~Coordinate} and
 * transforms it into a `{string}`.
 *
 * @typedef {function((Coordinate|undefined)): string} CoordinateFormat
 * @api
 */

/**
 * Add `delta` to `coordinate`. `coordinate` is modified in place and returned
 * by the function.
 *
 * Example:
 *
 *     import {add} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     add(coord, [-2, 4]);
 *     // coord is now [5.85, 51.983333]
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {Coordinate} delta Delta.
 * @return {Coordinate} The input coordinate adjusted by
 * the given delta.
 * @api
 */
function add(coordinate, delta) {
  coordinate[0] += +delta[0];
  coordinate[1] += +delta[1];
  return coordinate;
}
/**
 * Calculates the point closest to the passed coordinate on the passed circle.
 *
 * @param {Coordinate} coordinate The coordinate.
 * @param {import("./geom/Circle.js").default} circle The circle.
 * @return {Coordinate} Closest point on the circumference.
 */


function closestOnCircle(coordinate, circle) {
  var r = circle.getRadius();
  var center = circle.getCenter();
  var x0 = center[0];
  var y0 = center[1];
  var x1 = coordinate[0];
  var y1 = coordinate[1];
  var dx = x1 - x0;
  var dy = y1 - y0;

  if (dx === 0 && dy === 0) {
    dx = 1;
  }

  var d = Math.sqrt(dx * dx + dy * dy);
  var x = x0 + r * dx / d;
  var y = y0 + r * dy / d;
  return [x, y];
}
/**
 * Calculates the point closest to the passed coordinate on the passed segment.
 * This is the foot of the perpendicular of the coordinate to the segment when
 * the foot is on the segment, or the closest segment coordinate when the foot
 * is outside the segment.
 *
 * @param {Coordinate} coordinate The coordinate.
 * @param {Array<Coordinate>} segment The two coordinates
 * of the segment.
 * @return {Coordinate} The foot of the perpendicular of
 * the coordinate to the segment.
 */


function closestOnSegment(coordinate, segment) {
  var x0 = coordinate[0];
  var y0 = coordinate[1];
  var start = segment[0];
  var end = segment[1];
  var x1 = start[0];
  var y1 = start[1];
  var x2 = end[0];
  var y2 = end[1];
  var dx = x2 - x1;
  var dy = y2 - y1;
  var along = dx === 0 && dy === 0 ? 0 : (dx * (x0 - x1) + dy * (y0 - y1)) / (dx * dx + dy * dy || 0);
  var x, y;

  if (along <= 0) {
    x = x1;
    y = y1;
  } else if (along >= 1) {
    x = x2;
    y = y2;
  } else {
    x = x1 + along * dx;
    y = y1 + along * dy;
  }

  return [x, y];
}
/**
 * Returns a {@link module:ol/coordinate~CoordinateFormat} function that can be
 * used to format
 * a {Coordinate} to a string.
 *
 * Example without specifying the fractional digits:
 *
 *     import {createStringXY} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var stringifyFunc = createStringXY();
 *     var out = stringifyFunc(coord);
 *     // out is now '8, 48'
 *
 * Example with explicitly specifying 2 fractional digits:
 *
 *     import {createStringXY} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var stringifyFunc = createStringXY(2);
 *     var out = stringifyFunc(coord);
 *     // out is now '7.85, 47.98'
 *
 * @param {number} [opt_fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {CoordinateFormat} Coordinate format.
 * @api
 */


function createStringXY(opt_fractionDigits) {
  return (
    /**
     * @param {Coordinate} coordinate Coordinate.
     * @return {string} String XY.
     */
    function (coordinate) {
      return toStringXY(coordinate, opt_fractionDigits);
    }
  );
}
/**
 * @param {string} hemispheres Hemispheres.
 * @param {number} degrees Degrees.
 * @param {number} [opt_fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} String.
 */


function degreesToStringHDMS(hemispheres, degrees, opt_fractionDigits) {
  var normalizedDegrees = (0, _math.modulo)(degrees + 180, 360) - 180;
  var x = Math.abs(3600 * normalizedDegrees);
  var dflPrecision = opt_fractionDigits || 0;
  var precision = Math.pow(10, dflPrecision);
  var deg = Math.floor(x / 3600);
  var min = Math.floor((x - deg * 3600) / 60);
  var sec = x - deg * 3600 - min * 60;
  sec = Math.ceil(sec * precision) / precision;

  if (sec >= 60) {
    sec = 0;
    min += 1;
  }

  if (min >= 60) {
    min = 0;
    deg += 1;
  }

  return deg + "\xB0 " + (0, _string.padNumber)(min, 2) + "\u2032 " + (0, _string.padNumber)(sec, 2, dflPrecision) + "\u2033" + (normalizedDegrees == 0 ? '' : ' ' + hemispheres.charAt(normalizedDegrees < 0 ? 1 : 0));
}
/**
 * Transforms the given {@link module:ol/coordinate~Coordinate} to a string
 * using the given string template. The strings `{x}` and `{y}` in the template
 * will be replaced with the first and second coordinate values respectively.
 *
 * Example without specifying the fractional digits:
 *
 *     import {format} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var template = 'Coordinate is ({x}|{y}).';
 *     var out = format(coord, template);
 *     // out is now 'Coordinate is (8|48).'
 *
 * Example explicitly specifying the fractional digits:
 *
 *     import {format} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var template = 'Coordinate is ({x}|{y}).';
 *     var out = format(coord, template, 2);
 *     // out is now 'Coordinate is (7.85|47.98).'
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {string} template A template string with `{x}` and `{y}` placeholders
 *     that will be replaced by first and second coordinate values.
 * @param {number} [opt_fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} Formatted coordinate.
 * @api
 */


function format(coordinate, template, opt_fractionDigits) {
  if (coordinate) {
    return template.replace('{x}', coordinate[0].toFixed(opt_fractionDigits)).replace('{y}', coordinate[1].toFixed(opt_fractionDigits));
  } else {
    return '';
  }
}
/**
 * @param {Coordinate} coordinate1 First coordinate.
 * @param {Coordinate} coordinate2 Second coordinate.
 * @return {boolean} The two coordinates are equal.
 */


function equals(coordinate1, coordinate2) {
  var equals = true;

  for (var i = coordinate1.length - 1; i >= 0; --i) {
    if (coordinate1[i] != coordinate2[i]) {
      equals = false;
      break;
    }
  }

  return equals;
}
/**
 * Rotate `coordinate` by `angle`. `coordinate` is modified in place and
 * returned by the function.
 *
 * Example:
 *
 *     import {rotate} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var rotateRadians = Math.PI / 2; // 90 degrees
 *     rotate(coord, rotateRadians);
 *     // coord is now [-47.983333, 7.85]
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {number} angle Angle in radian.
 * @return {Coordinate} Coordinate.
 * @api
 */


function rotate(coordinate, angle) {
  var cosAngle = Math.cos(angle);
  var sinAngle = Math.sin(angle);
  var x = coordinate[0] * cosAngle - coordinate[1] * sinAngle;
  var y = coordinate[1] * cosAngle + coordinate[0] * sinAngle;
  coordinate[0] = x;
  coordinate[1] = y;
  return coordinate;
}
/**
 * Scale `coordinate` by `scale`. `coordinate` is modified in place and returned
 * by the function.
 *
 * Example:
 *
 *     import {scale as scaleCoordinate} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var scale = 1.2;
 *     scaleCoordinate(coord, scale);
 *     // coord is now [9.42, 57.5799996]
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {number} scale Scale factor.
 * @return {Coordinate} Coordinate.
 */


function scale(coordinate, scale) {
  coordinate[0] *= scale;
  coordinate[1] *= scale;
  return coordinate;
}
/**
 * @param {Coordinate} coord1 First coordinate.
 * @param {Coordinate} coord2 Second coordinate.
 * @return {number} Squared distance between coord1 and coord2.
 */


function squaredDistance(coord1, coord2) {
  var dx = coord1[0] - coord2[0];
  var dy = coord1[1] - coord2[1];
  return dx * dx + dy * dy;
}
/**
 * @param {Coordinate} coord1 First coordinate.
 * @param {Coordinate} coord2 Second coordinate.
 * @return {number} Distance between coord1 and coord2.
 */


function distance(coord1, coord2) {
  return Math.sqrt(squaredDistance(coord1, coord2));
}
/**
 * Calculate the squared distance from a coordinate to a line segment.
 *
 * @param {Coordinate} coordinate Coordinate of the point.
 * @param {Array<Coordinate>} segment Line segment (2
 * coordinates).
 * @return {number} Squared distance from the point to the line segment.
 */


function squaredDistanceToSegment(coordinate, segment) {
  return squaredDistance(coordinate, closestOnSegment(coordinate, segment));
}
/**
 * Format a geographic coordinate with the hemisphere, degrees, minutes, and
 * seconds.
 *
 * Example without specifying fractional digits:
 *
 *     import {toStringHDMS} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var out = toStringHDMS(coord);
 *     // out is now '47° 58′ 60″ N 7° 50′ 60″ E'
 *
 * Example explicitly specifying 1 fractional digit:
 *
 *     import {toStringHDMS} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var out = toStringHDMS(coord, 1);
 *     // out is now '47° 58′ 60.0″ N 7° 50′ 60.0″ E'
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {number} [opt_fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} Hemisphere, degrees, minutes and seconds.
 * @api
 */


function toStringHDMS(coordinate, opt_fractionDigits) {
  if (coordinate) {
    return degreesToStringHDMS('NS', coordinate[1], opt_fractionDigits) + ' ' + degreesToStringHDMS('EW', coordinate[0], opt_fractionDigits);
  } else {
    return '';
  }
}
/**
 * Format a coordinate as a comma delimited string.
 *
 * Example without specifying fractional digits:
 *
 *     import {toStringXY} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var out = toStringXY(coord);
 *     // out is now '8, 48'
 *
 * Example explicitly specifying 1 fractional digit:
 *
 *     import {toStringXY} from 'ol/coordinate';
 *
 *     var coord = [7.85, 47.983333];
 *     var out = toStringXY(coord, 1);
 *     // out is now '7.8, 48.0'
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {number} [opt_fractionDigits] The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} XY.
 * @api
 */


function toStringXY(coordinate, opt_fractionDigits) {
  return format(coordinate, '{x}, {y}', opt_fractionDigits);
}
/**
 * Modifies the provided coordinate in-place to be within the real world
 * extent. The lower projection extent boundary is inclusive, the upper one
 * exclusive.
 *
 * @param {Coordinate} coordinate Coordinate.
 * @param {import("./proj/Projection.js").default} projection Projection.
 * @return {Coordinate} The coordinate within the real world extent.
 */


function wrapX(coordinate, projection) {
  if (projection.canWrapX()) {
    var worldWidth = (0, _extent.getWidth)(projection.getExtent());
    var worldsAway = getWorldsAway(coordinate, projection, worldWidth);

    if (worldsAway) {
      coordinate[0] -= worldsAway * worldWidth;
    }
  }

  return coordinate;
}
/**
 * @param {Coordinate} coordinate Coordinate.
 * @param {import("./proj/Projection.js").default} projection Projection.
 * @param {number} [opt_sourceExtentWidth] Width of the source extent.
 * @return {number} Offset in world widths.
 */


function getWorldsAway(coordinate, projection, opt_sourceExtentWidth) {
  var projectionExtent = projection.getExtent();
  var worldsAway = 0;

  if (projection.canWrapX() && (coordinate[0] < projectionExtent[0] || coordinate[0] > projectionExtent[2])) {
    var sourceExtentWidth = opt_sourceExtentWidth || (0, _extent.getWidth)(projectionExtent);
    worldsAway = Math.floor((coordinate[0] - projectionExtent[0]) / sourceExtentWidth);
  }

  return worldsAway;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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